// Header behavior (<store-header>), kept intentionally small.
// The mobile menu is a native <details> holding only the summary; the
// panel and backdrop are SIBLINGS driven by [open] ~ in CSS. That
// placement is what makes the menu animate on Safari in both directions:
// WebKit refuses transitions on elements it just rendered, and the
// content of a closed details is not rendered at all. As siblings they
// are rendered from page load, so opening and closing are one plain CSS
// transition each, no script in the loop.
//
// What is left here:
// - measure --drawer-top BEFORE the panel opens (summary click), so it
//   never starts sliding against the fallback top and jumps;
// - close the menu on backdrop click and on Escape (focus returns to the
//   toggle);
// - the search dialog. Its EXIT must run before the real close: Safari
//   has no `overlay`, so close() ejects a dialog from the top layer that
//   same frame, and any exit transition only paints while it is open.
// - on Escape outside the menu, blur the focused element so the CSS
//   :focus-within desktop dropdown closes for keyboard users.
// - publish the height of the fixed header group (announcement strip plus
//   bar) as --header-height on :root, so anything else that sticks to the
//   top stops under it.

// Covers a transition that never fires (display: none ancestors, etc.).
const CLOSE_FALLBACK = 400;

// While a panel is open the rest of the page sits behind a backdrop, so
// it has to be behind for the keyboard too, or Tab walks straight into
// content nobody can see.
//
// Regions rather than the usual "inert every sibling" walk, and on
// purpose: the controls that DRIVE a panel are siblings of it, and
// inerting them would break the very thing that closes the panel. Naming
// what is behind is both simpler and safer.
//
// Duplicated in results.js so each module stays self-contained, the same
// deal as closeDialog: change one, change the other.
function setPageInert(selectors, on) {
  selectors.forEach((selector) => {
    const region = document.querySelector(selector);
    if (!region) return;
    if (on) region.setAttribute('inert', '');
    else region.removeAttribute('inert');
  });
}

// Plays the exit while the dialog is still open, then really closes it.
// Duplicated in cart.js and quick-view.js on purpose, so each module
// stays self-contained: change one, change the others.
function closeDialog(dialog) {
  if (!dialog?.open || dialog.hasAttribute('closing')) return;
  dialog.setAttribute('closing', '');
  const finish = () => {
    clearTimeout(timer);
    dialog.removeEventListener('transitionend', finish);
    dialog.removeAttribute('closing');
    dialog.close();
  };
  const timer = setTimeout(finish, CLOSE_FALLBACK);
  dialog.addEventListener('transitionend', finish);
}

class StoreHeader extends HTMLElement {
  connectedCallback() {
    this.menu = this.querySelector('[data-menu-details]');
    this.searchDialog = this.querySelector('[data-search-dialog]');

    this.handleClick = (event) => {
      if (event.target.closest('[data-drawer-backdrop]')) this.closeMenu();

      // The X lives inside the panel in this design, not on the bar, so it
      // cannot be the summary. Closing from there needs this line; the
      // summary still opens and closes on its own with no JS.
      if (event.target.closest('[data-drawer-close]')) {
        this.closeMenu();
        this.menu?.querySelector('summary')?.focus();
      }

      // About to open: measure before the native toggle lands, so the
      // panel slides in already at the right top. The toggle itself stays
      // native, which is also the no-JS path.
      const toggle = event.target.closest('[data-menu-details] > summary');
      if (toggle && !this.menu?.open) this.setDrawerTop();

      // Upgrade the search link into a modal with an autofocused field;
      // without JS the same link still opens the search page.
      const opener = event.target.closest('[data-search-open]');
      if (opener && this.searchDialog) {
        event.preventDefault();
        this.closeMenu();
        this.searchDialog.showModal();
      }

      if (event.target.closest('[data-search-close]')) closeDialog(this.searchDialog);

      // A click on the dialog's own box is a click on the backdrop: the
      // box is the panel, so anything outside it is the overlay.
      if (event.target === this.searchDialog) closeDialog(this.searchDialog);
    };

    this.handleKeydown = (event) => {
      if (event.key !== 'Escape') return;
      if (this.menu?.open) {
        this.closeMenu();
        this.menu.querySelector('summary')?.focus();
        return;
      }
      const active = document.activeElement;
      if (active && this.contains(active) && active !== document.body) active.blur();
    };

    // Escape on the search dialog fires cancel: hold the instant close
    // and play the exit instead.
    this.handleCancel = (event) => {
      event.preventDefault();
      closeDialog(this.searchDialog);
    };

    // Safety net for opens that skip the summary click (programmatic).
    // toggle fires in both directions, including when closeMenu removes
    // the attribute, so the inert flip rides along here.
    this.handleToggle = () => {
      const open = this.menu?.open === true;
      if (open) this.setDrawerTop();
      setPageInert(['main', 'footer'], open);
    };

    // Um AbortController para todos, como no resto do tema: um listener
    // novo aqui não precisa lembrar de nada no disconnect.
    this.listeners = new AbortController();
    const { signal } = this.listeners;

    this.addEventListener('click', this.handleClick, { signal });
    document.addEventListener('keydown', this.handleKeydown, { signal });
    this.menu?.addEventListener('toggle', this.handleToggle, { signal });
    this.searchDialog?.addEventListener('cancel', this.handleCancel, { signal });

    // A barra é fixa, então quem também gruda no topo (resumo do carrinho,
    // filtros laterais, âncoras) precisa saber onde ela termina para não
    // passar por baixo dela. Mede o grupo fixo inteiro, faixa de aviso
    // incluída. Medida e não constante: a altura muda do celular para o
    // desktop, e a faixa ganha uma segunda linha no celular estreito.
    const bar = this.closest('[data-header-group]') || this.closest('header') || this;
    this.resizeObserver = new ResizeObserver(() => {
      const height = Math.round(bar.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    });
    this.resizeObserver.observe(bar);
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.resizeObserver?.disconnect();
    // Sair da página com o menu aberto não pode deixar o resto inerte.
    setPageInert(['main', 'footer'], false);
  }

  // Where the bar actually ends right now, so the panel starts under it
  // even with sections above the header, like the marquee.
  setDrawerTop() {
    const header = this.closest('header') || this;
    const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    this.style.setProperty('--drawer-top', `${bottom}px`);
  }

  // Removing [open] is the whole close: the panel is a rendered sibling,
  // so CSS transitions it out on its own.
  closeMenu() {
    this.menu?.removeAttribute('open');
  }
}

if (!customElements.get('store-header')) {
  customElements.define('store-header', StoreHeader);
}
